import { useEffect, useMemo, type FC, type ReactNode } from "react";

import {
  Alert,
  Button,
  ClickIcon,
  Column,
  FilterIcon,
  HiddenFormLabel,
  IconBox,
  LightningBoltIcon,
  NumberInput,
  PlusIcon,
  Row,
  Select,
  Text,
  TimeIcon,
} from "@hightouch/htui";
import sortBy from "lodash/sortBy";
import pluralize from "pluralize";
import { isPresent } from "ts-extras";
import { v4 as uuidv4 } from "uuid";

import { isRealtimeEventModel } from "src/utils/event-models";
import { useFormErrorContext } from "src/contexts/form-error-context";
import {
  type EventCondition,
  eventOperatorOptions,
  initialFunnelCondition,
  initialEventWindow,
  ConditionType,
  PropertyComparisonType,
  initialRealtimeEventWindow,
  type NestableCondition,
  NumberOperator,
  type Audience,
  type Condition,
  type PropertyCondition,
} from "src/types/visual";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { isDenied } from "src/components/explore/restrictions";

import { FilterPopover } from "src/components/explore/filter-popover";
import {
  createSubconditionUpdater,
  type OnUpdate,
  removeSubconditionMutative,
  sortSubconditions,
} from "src/components/explore/utils/condition-builders";
import { formatSubconditions } from "src/components/explore/utils/format-subconditions";
import { isAndOrCondition } from "src/components/explore/utils/type-guards";
import { HStack } from "./shared";
import { ConditionField, type FilterProps } from "./condition";
import { DetailButton, WhereButton } from "./condition-buttons";
import { ConditionTextWrapper } from "./condition-text-wrapper";
import {
  REALTIME_EVENT_COUNT_MAX,
  realtimeEventCountCapHint,
  validateEventCondition,
} from "./condition-validation";
import { ErrorMessage } from "./error-message";
import { Filter } from "./filter";
import { FunnelFilter } from "./funnel-filter";
import { NestedPropertyFilter } from "./nested-property-filter";
import { WindowFilter } from "./window-filter";
import { FilterableOptionsProvider } from "src/components/explore/filter-popover/filterable-options-context";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import {
  FilterOption,
  MESSAGE_EVENT_CHANNEL_ICON_OVERRIDES,
  getEventSubconditionFilterableSections,
} from "src/components/explore/filter-popover/constants";
import { getManagedModelChannel } from "src/cep/utils";
import type { MinimalParentModel } from "src/components/audiences/types";
import { NestedRelationConditionWrapper } from "./condition-wrapper";
import type { Updater } from "src/utils/immutable-helpers";
import { WhereEachText } from "./related-filter-shared";
import { FieldError } from "src/components/field-error";
import { useNestedFilteringFlag } from "src/components/explore/utils/computed-flag";
import { CepManagedModelType } from "src/cep/types";

const RECEIVED_TYPE_FOR_OPEN_OR_CLICK: Partial<
  Record<CepManagedModelType, CepManagedModelType>
> = {
  [CepManagedModelType.EmailOpened]: CepManagedModelType.EmailReceived,
  [CepManagedModelType.EmailClicked]: CepManagedModelType.EmailReceived,
  [CepManagedModelType.SmsRead]: CepManagedModelType.SmsReceived,
  [CepManagedModelType.SmsClicked]: CepManagedModelType.SmsReceived,
  [CepManagedModelType.WhatsappRead]: CepManagedModelType.WhatsappReceived,
  [CepManagedModelType.WhatsappClicked]: CepManagedModelType.WhatsappReceived,
  [CepManagedModelType.PushOpened]: CepManagedModelType.PushReceived,
};

// Placeholder ID for the "New event model" picker option.
const CREATE_EVENT_SENTINEL = "__create_event__";

type EventPickerValue = {
  eventModelId: string | null;
  relationshipId: string | null;
};

type EventFilterProps = {
  disableChangingEvent?: boolean;
  disableEventsWithoutPrimaryKey?: boolean;

  showOnlyEventOptions?: boolean;
  createEventOption?: { label: string; onSelect: () => void };
  afterEventPicker?: ReactNode;
  isDefiningNewEvent?: boolean;

  // Temporary for v1 of the metrics form
  hideFunnelCondition?: boolean;
  hideWindowCondition?: boolean;
  hideOperatorFilter?: boolean;
  allowParameterization?: boolean;
  // Replaces the "Where each ... performed" lead-in when the picker chooses an event rather than filtering on one.
  eventLabel?: string;
};

export const EventFilter: FC<
  Readonly<FilterProps<EventCondition> & EventFilterProps>
> = ({
  disableChangingEvent = false,
  disableEventsWithoutPrimaryKey = false,
  hideFunnelCondition: hideFunnelConditionProp,
  hideWindowCondition,
  hideOperatorFilter,
  showOnlyEventOptions = false,
  createEventOption,
  afterEventPicker,
  isDefiningNewEvent = false,
  allowParameterization,
  eventLabel,
  ...props
}) => {
  const { condition: unformattedCondition, onUpdate, isEventTrait } = props;
  const filterId = useMemo<string>(uuidv4, []);
  const queryBuilderNestedFiltering = useNestedFilteringFlag();
  const { restrictions, enableNestedFiltering, realtimeConditionScope } =
    useQueryBuilderContext();

  const isRealtimeAudience = realtimeConditionScope === "audience";

  const isNestedFilteringEnabled =
    queryBuilderNestedFiltering && enableNestedFiltering;

  const {
    events: allEvents,
    messageEvents,
    nestingLevel,
    modelName,
    parentFilterOptionType,
    parentIconOverride,
  } = useFilterableOptionsContext();

  const hideFunnelCondition =
    hideFunnelConditionProp || isDenied(restrictions, "funnels");

  // Batch entry cannot query stream events (and Schema has no PK UI for them),
  // so omit realtime models instead of listing them as disabled.
  const events = sortBy(
    disableEventsWithoutPrimaryKey
      ? allEvents.filter((event) => !isRealtimeEventModel(event.to_model.event))
      : allEvents,
    ["to_model.name", "to_model.event.primary_key"],
  );

  const maybeCreateEventOption = createEventOption
    ? [
        {
          label: createEventOption.label,
          description: "",
          value: {
            eventModelId: CREATE_EVENT_SENTINEL,
            relationshipId: CREATE_EVENT_SENTINEL,
          },
          event: null,
        },
      ]
    : [];

  const eventPickerOptions: {
    label: string;
    description: string;
    value: EventPickerValue;
    event: (typeof events)[number] | null;
  }[] = [
    ...maybeCreateEventOption,
    ...events.map((event) => ({
      label: event.to_model.name,
      description:
        disableEventsWithoutPrimaryKey && !event.to_model.event?.primary_key
          ? "This event has no primary key configured"
          : (event.to_model.description ?? ""),
      value: {
        eventModelId: event.to_model.id,
        relationshipId: event.id,
      },
      event,
    })),
  ];

  const event = events.find(
    (event) =>
      event.to_model.id.toString() ===
        unformattedCondition.eventModelId?.toString() &&
      event.id.toString() === unformattedCondition.relationshipId?.toString(),
  );

  const messageEvent = messageEvents.find(
    (e) =>
      e.to_model.id.toString() ===
        unformattedCondition.eventModelId?.toString() &&
      e.id.toString() === unformattedCondition.relationshipId?.toString(),
  );

  const messageEventChannel = getManagedModelChannel(
    messageEvent?.to_model.cep_managed_model?.type,
  );
  const messageEventIconOverride = messageEventChannel
    ? MESSAGE_EVENT_CHANNEL_ICON_OVERRIDES[messageEventChannel]
    : undefined;

  const currentManagedModelType =
    messageEvent?.to_model.cep_managed_model?.type;
  const receivedType = currentManagedModelType
    ? RECEIVED_TYPE_FOR_OPEN_OR_CLICK[currentManagedModelType]
    : undefined;
  const receivedMessageEvent = receivedType
    ? messageEvents.find(
        (e) => e.to_model.cep_managed_model?.type === receivedType,
      )
    : undefined;
  const isOpenedOrClickedEvent = receivedType !== undefined;

  const isRealtimeEvent =
    isDefiningNewEvent || isRealtimeEventModel(event?.to_model.event);

  // For message events, override the property section icon to use the channel icon
  const eventSubconditionSections = useMemo(() => {
    const baseSections = getEventSubconditionFilterableSections({
      enableNestedFiltering: isNestedFilteringEnabled,
      isRealtimeAudience,
    });
    if (!messageEventIconOverride) return baseSections;
    return baseSections.map((section) =>
      section.type === FilterOption.Property
        ? {
            ...section,
            icon: messageEventIconOverride.icon,
            shade: messageEventIconOverride.color,
          }
        : section,
    );
  }, [messageEventIconOverride, isNestedFilteringEnabled, isRealtimeAudience]);

  // Wrap subconditions in one and/or condition
  const condition = formatSubconditions(unformattedCondition);
  const topLevelSubcondition = condition.subconditions?.[0];
  // Assumption is that event condition will be formatted to always have one And/Or subcondition as a child (if there are subconditions)
  const subconditions = isAndOrCondition(topLevelSubcondition)
    ? topLevelSubcondition.conditions
    : [];

  const { getErrors, setFieldError, removeErrors } = useFormErrorContext();

  const filterErrors = getErrors(filterId, {
    // Journey event pickers (`showOnlyEventOptions`) live in a submit-gated
    // form. Showing every recorded error immediately paints "This field is
    // required" on an empty event select before the author has tried to save.
    allErrors: !showOnlyEventOptions,
  });
  const eventError = filterErrors?.eventModelId;
  const valueError = filterErrors?.value;
  const windowError = filterErrors?.window;
  const conditionError = filterErrors?.condition;

  const isNested = nestingLevel > 0;

  // Realtime audiences don't support nested conditions
  const unsupportedCondition = isRealtimeAudience && isNested;

  // A surface that hides the window control is asserting the window is not
  // part of this condition's meaning, so it must not then be validated as
  // required.
  const requiresRealtimeWindow = isRealtimeEvent && !hideWindowCondition;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    const conditionErrors = validateEventCondition(condition, {
      ignoreValue: showOnlyEventOptions,
      isRealtimeEvent: requiresRealtimeWindow,
      isRealtimeAudience: unsupportedCondition,
      maxEventCount: isRealtimeAudience ? REALTIME_EVENT_COUNT_MAX : undefined,
    });
    setFieldError(
      filterId,
      isDefiningNewEvent
        ? { ...conditionErrors, eventModelId: null }
        : conditionErrors,
    );

    return () => {
      removeErrors([filterId]);
    };
  }, [
    removeErrors,
    setFieldError,
    showOnlyEventOptions,
    isDefiningNewEvent,
    filterId,
    requiresRealtimeWindow,
    isRealtimeAudience,
    // `condition` is not stable between renders, so watch the
    // individual fields that are used in the validation
    condition.eventModelId,
    condition.relationshipId,
    condition.value,
    condition.window,
    unsupportedCondition,
  ]);

  const operatorLabel = eventOperatorOptions.find(
    ({ value }) => value === condition.operator,
  )?.label;

  const updateWrappingConditionType = (
    type: ConditionType.And | ConditionType.Or,
  ) => {
    if (!isAndOrCondition(topLevelSubcondition)) {
      return;
    }

    onUpdate((c) => {
      c.subconditions = [
        {
          type: type,
          conditions: topLevelSubcondition?.conditions ?? [],
          id: topLevelSubcondition?.id ?? crypto.randomUUID(),
        },
      ];
    });
  };

  const addSubcondition = (newCondition: NestableCondition) => {
    if (!isAndOrCondition(topLevelSubcondition)) {
      return;
    }

    const newSubconditionsList = [
      {
        type: topLevelSubcondition.type,
        id: topLevelSubcondition.id,
        conditions: [...topLevelSubcondition.conditions, newCondition].toSorted(
          sortSubconditions,
        ),
      },
      // For aesthetic purposes move small property conditions above large related/event conditions
    ];

    onUpdate((c) => {
      c.subconditions = newSubconditionsList;
    });
  };

  const generateOnUpdate = useMemo(
    () => createSubconditionUpdater(onUpdate),
    [onUpdate],
  );

  const removeSubcondition = (id?: string) => {
    onUpdate((condition) => removeSubconditionMutative(condition, id));
  };

  const noWindowCondition = !hideWindowCondition && !condition.window;

  return (
    <>
      <HStack gap={2}>
        {eventLabel != null ? (
          <ConditionTextWrapper>
            <Text color="text.secondary">{eventLabel}</Text>
          </ConditionTextWrapper>
        ) : (
          <>
            {isNested && (
              <WhereEachText
                modelName={modelName}
                filterOptionType={parentFilterOptionType}
                iconOverride={parentIconOverride}
              />
            )}
            <ConditionTextWrapper>
              <Text color="text.secondary">{isNested ? "p" : "P"}erformed</Text>
            </ConditionTextWrapper>
          </>
        )}
        {showOnlyEventOptions || isEventTrait ? (
          <Column>
            <Select
              isClearable={!isDefiningNewEvent}
              isDisabled={props.isDisabled}
              isInvalid={Boolean(eventError)}
              placeholder="Select an event"
              isOptionDisabled={(option) =>
                option.event != null &&
                disableEventsWithoutPrimaryKey &&
                !option.event.to_model.event?.primary_key
              }
              options={eventPickerOptions}
              optionValue={(option) => option.value}
              optionLabel={(option) => option.label}
              optionAccessories={(option) => ({
                leftAccessory: {
                  type: "icon",
                  icon: () => (
                    <IconBox
                      color="teal"
                      size="sm"
                      icon={
                        option.event == null ? (
                          <PlusIcon />
                        ) : isRealtimeEventModel(
                            option.event.to_model.event,
                          ) ? (
                          <LightningBoltIcon />
                        ) : (
                          <ClickIcon />
                        )
                      }
                    />
                  ),
                },
              })}
              optionDescription={(option) => option.description}
              width="3xs"
              value={
                isDefiningNewEvent
                  ? {
                      eventModelId: CREATE_EVENT_SENTINEL,
                      relationshipId: CREATE_EVENT_SENTINEL,
                    }
                  : {
                      eventModelId: condition.eventModelId,
                      relationshipId: condition.relationshipId,
                    }
              }
              onChange={(changes) => {
                if (changes?.eventModelId === CREATE_EVENT_SENTINEL) {
                  if (!isDefiningNewEvent) {
                    createEventOption?.onSelect();
                  }
                  return;
                }
                onUpdate((condition) => {
                  if (!changes) {
                    condition.eventModelId = null;
                    condition.relationshipId = null;
                  } else {
                    condition.eventModelId = changes.eventModelId;
                    condition.relationshipId = changes.relationshipId;
                  }
                });
              }}
            />
            {eventError && <ErrorMessage>{eventError}</ErrorMessage>}
          </Column>
        ) : (
          <FilterPopover
            {...props}
            condition={condition}
            isDisabled={disableChangingEvent || props.isDisabled}
            hasError={Boolean(eventError)}
            onChange={(newCondition) => onUpdate(() => newCondition)}
          />
        )}
        {hideOperatorFilter
          ? null
          : !isEventTrait && (
              <>
                <Filter
                  isEmpty={false} // Field is automatically populated
                  content={
                    <Row gap={2}>
                      <Select
                        removePortal
                        isDisabled={props.isDisabled}
                        options={eventOperatorOptions}
                        placeholder="Filter on"
                        value={condition.operator}
                        width="auto"
                        onChange={(operator) => {
                          if (operator) {
                            onUpdate((condition) => {
                              condition.operator = operator;
                            });
                          }
                        }}
                      />
                      <Column>
                        <HiddenFormLabel label="Amount">
                          <NumberInput
                            isDisabled={props.isDisabled}
                            isInvalid={Boolean(valueError)}
                            placeholder="amount…"
                            width="auto"
                            min={0}
                            max={
                              isRealtimeAudience
                                ? REALTIME_EVENT_COUNT_MAX
                                : undefined
                            }
                            value={condition.value ?? undefined}
                            onChange={(value) => {
                              if (value == null) {
                                return;
                              }
                              onUpdate((condition) => {
                                condition.value =
                                  isRealtimeAudience &&
                                  value > REALTIME_EVENT_COUNT_MAX
                                    ? REALTIME_EVENT_COUNT_MAX
                                    : value;
                              });
                            }}
                          />
                        </HiddenFormLabel>
                        {valueError && (
                          <ErrorMessage>{valueError}</ErrorMessage>
                        )}
                        {!valueError &&
                          isRealtimeAudience &&
                          Number(condition.value) >=
                            REALTIME_EVENT_COUNT_MAX && (
                            <Text color="text.secondary" mt={2} size="sm">
                              {realtimeEventCountCapHint}
                            </Text>
                          )}
                      </Column>
                    </Row>
                  }
                  error={valueError}
                >
                  <Text fontWeight="medium">
                    <Text color="text.secondary" fontWeight="normal">
                      {operatorLabel}
                    </Text>{" "}
                    {isPresent(condition.value)
                      ? `${condition.value} ${pluralize(
                          "time",
                          Number(condition.value),
                        )}`
                      : "___ times"}
                  </Text>
                </Filter>
              </>
            )}
      </HStack>

      {afterEventPicker}

      {!condition.window && windowError && (
        <ErrorMessage>{windowError}</ErrorMessage>
      )}

      {!hideWindowCondition && condition.window && (
        <WindowFilter
          isRealtimeEvent={isRealtimeEvent}
          isDisabled={props.isDisabled}
          condition={condition}
          onUpdate={onUpdate}
        />
      )}
      {(event || condition.eventModelId || isDefiningNewEvent) && (
        <Column pl={8} gap={4} mt={2}>
          <FilterableOptionsProvider
            modelId={condition.eventModelId}
            sections={eventSubconditionSections}
            parentFilterOptionType={
              messageEventIconOverride
                ? FilterOption.MessageEvent
                : isRealtimeEvent
                  ? FilterOption.RealtimeEvent
                  : FilterOption.Event
            }
            parentIconOverride={
              messageEventIconOverride
                ? { ...messageEventIconOverride, variant: "inverted" as const }
                : undefined
            }
          >
            {subconditions.map((subcondition, index) => (
              <EventSubcondition
                key={subcondition.id ?? index}
                subcondition={subcondition}
                isFirstCondition={index === 0}
                allowParameterization={allowParameterization}
                audience={props.audience}
                parent={props.parent}
                isRealtimeEvent={isRealtimeEvent}
                isWrappedWithAndCondition={
                  topLevelSubcondition?.type === ConditionType.And
                }
                generateOnUpdate={generateOnUpdate}
                isDisabled={props.isDisabled}
                onRemove={() => removeSubcondition(subcondition.id)}
                onToggleWrappingConditionType={() =>
                  updateWrappingConditionType(
                    topLevelSubcondition?.type === ConditionType.And
                      ? ConditionType.Or
                      : ConditionType.And,
                  )
                }
              />
            ))}

            <Row gap={4}>
              <WhereButton
                isDisabled={props.isDisabled}
                // TODO(nolan) fix types
                parent={event?.to_model as unknown as MinimalParentModel}
                // Is there a way to constrain filter popover's return types to the context?
                onChange={addSubcondition as (newCondition: Condition) => void}
              />
              {noWindowCondition && (
                <DetailButton
                  isDisabled={props.isDisabled}
                  icon={TimeIcon}
                  size="sm"
                  onClick={() => {
                    onUpdate((condition) => {
                      condition.window = isRealtimeEvent
                        ? initialRealtimeEventWindow
                        : initialEventWindow;
                    });
                  }}
                >
                  Time window
                </DetailButton>
              )}

              {!hideFunnelCondition && !condition.funnelCondition ? (
                <DetailButton
                  isDisabled={props.isDisabled}
                  icon={FilterIcon}
                  size="sm"
                  onClick={() => {
                    onUpdate((condition) => {
                      condition.funnelCondition = {
                        ...initialFunnelCondition,
                        didPerform: true,
                      };
                    });
                  }}
                >
                  Then performed…
                </DetailButton>
              ) : null}
            </Row>
          </FilterableOptionsProvider>
        </Column>
      )}
      {!hideFunnelCondition && isPresent(condition.funnelCondition) && (
        <FunnelFilter
          {...props}
          condition={condition.funnelCondition}
          eventCondition={condition}
          onUpdate={(updater) => {
            onUpdate((condition) => {
              if (condition.funnelCondition) {
                const result = updater(condition.funnelCondition!);
                if (result) {
                  condition.funnelCondition = result;
                }
              }
            });
          }}
          onRemove={() =>
            onUpdate((condition) => {
              condition.funnelCondition = undefined;
            })
          }
        />
      )}

      {isOpenedOrClickedEvent &&
        condition.value === 0 &&
        !condition.funnelCondition &&
        receivedMessageEvent && (
          <Alert
            type="warning"
            title="Do you want to include profiles who received the message?"
            message="Filtering on this model only will include profiles who did not receive the message at all"
            actions={
              <Button
                variant="secondary"
                onClick={() => {
                  onUpdate((c) => {
                    const originalEventModelId = c.eventModelId;
                    const originalRelationshipId = c.relationshipId;
                    // Change the event condition to be "received" message event
                    c.eventModelId = receivedMessageEvent.to_model.id;
                    c.relationshipId = receivedMessageEvent.id;
                    c.operator = NumberOperator.GreaterThanOrEqualTo;
                    c.value = 1;

                    // Move the original condition to be a "did not" funnel
                    c.funnelCondition = {
                      ...initialFunnelCondition,
                      eventModelId: originalEventModelId,
                      relationshipId: originalRelationshipId,
                      didPerform: false,
                    };
                  });
                }}
              >
                Yes, add "received" filter
              </Button>
            }
          />
        )}

      {conditionError && <FieldError error={conditionError} />}
    </>
  );
};

interface EventSubconditionProps {
  subcondition: NestableCondition;
  allowParameterization?: boolean;
  isFirstCondition: boolean;
  isRealtimeEvent: boolean;
  isWrappedWithAndCondition: boolean;
  generateOnUpdate: (id?: string) => OnUpdate<NestableCondition>;
  isDisabled?: boolean;
  onRemove: () => void;
  onToggleWrappingConditionType: () => void;
  audience?: Audience;
  parent?: MinimalParentModel | null;
}

const EventSubcondition = (props: EventSubconditionProps) => {
  const {
    subcondition,
    audience,
    parent,
    allowParameterization,
    isFirstCondition,
    isRealtimeEvent,
    isWrappedWithAndCondition,
    generateOnUpdate,
    isDisabled,
    onRemove,
    onToggleWrappingConditionType,
  } = props;
  const { enableReferencingEntryEvent, enableNestedFiltering } =
    useQueryBuilderContext();
  const queryBuilderNestedFiltering = useNestedFilteringFlag();
  const isNestedFilteringEnabled =
    enableNestedFiltering && queryBuilderNestedFiltering;

  const onUpdate = useMemo(
    () => generateOnUpdate(subcondition.id),
    [generateOnUpdate, subcondition.id],
  );

  if (subcondition.type !== ConditionType.Property) {
    if (!isNestedFilteringEnabled) {
      return null;
    }
    return (
      <NestedRelationConditionWrapper
        isFirstCondition={isFirstCondition}
        isWrappedWithAndCondition={isWrappedWithAndCondition}
        onToggleWrappingConditionType={onToggleWrappingConditionType}
        onRemove={onRemove}
      >
        <ConditionField
          condition={subcondition}
          parent={parent}
          audience={audience}
          isDisabled={isDisabled}
          // Currently we don't have a way to limit ConditionField to only NestableCondition types
          onUpdate={onUpdate as (updater: Updater<Condition>) => void}
          onRemove={onRemove}
        />
      </NestedRelationConditionWrapper>
    );
  }

  return (
    <NestedPropertyFilter
      lhsPropertyFilterVariant="dropdown"
      columns={undefined}
      showEventPropertyTypeDriftWarning
      audience={audience}
      parent={parent}
      propertyComparisonType={
        isRealtimeEvent
          ? PropertyComparisonType.None
          : enableReferencingEntryEvent
            ? PropertyComparisonType.JourneyEntryEvent
            : PropertyComparisonType.Default
      }
      includeParentInPropertyComparison={false}
      showParameterizeCheckbox={allowParameterization}
      condition={subcondition}
      isDisabled={isDisabled}
      isFirstCondition={isFirstCondition}
      isWrappedWithAndCondition={isWrappedWithAndCondition}
      onUpdate={onUpdate as OnUpdate<PropertyCondition>}
      onRemove={onRemove}
      onToggleWrappingConditionType={onToggleWrappingConditionType}
      containerSx={{ alignItems: "flex-start", width: "100%" }}
    />
  );
};
